import { useCallback, useEffect, useRef, useState } from 'react';

export type AsyncState<T> =
  { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: T };

/**
 * Loads on mount and whenever deps change; reload() retries. Keeps showing the previous data
 * while refetching so filtering doesn't flash. Consumers must render all three states.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: unknown[] = [],
): [AsyncState<T>, () => void] {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let live = true;
    loadRef.current().then(
      (data) => live && setState({ status: 'ready', data }),
      (e: unknown) =>
        live && setState({ status: 'error', message: e instanceof Error ? e.message : String(e) }),
    );
    return () => {
      live = false;
    };
  }, [tick, ...deps]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return [state, reload];
}
